import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import {
  CalendarDays, Clock, Plus, Trash2, Edit2, 
  Send, Bell, Save, X, Sparkles, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import CreateExhibitionEventModal from "./CreateExhibitionEventModal";

interface ExhibitionEvent {
  id: string;
  name: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

const AdminEventScheduler: React.FC = () => {
  const { user } = useAuthContext();
  const [events, setEvents] = useState<ExhibitionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ExhibitionEvent | null>(null);

  // Form state for editing
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    isActive: false,
    sendNotification: true,
  });

  const fetchEvents = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("exhibition_events")
        .select("*")
        .order("starts_at", { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      isActive: false,
      sendNotification: true,
    });
    setEditingEvent(null);
  };

  const handleEditEvent = (event: ExhibitionEvent) => {
    const startDate = new Date(event.starts_at);
    const endDate = new Date(event.ends_at);
    
    setFormData({
      name: event.name,
      description: event.description || "",
      startDate: format(startDate, "yyyy-MM-dd"),
      startTime: format(startDate, "HH:mm"),
      endDate: format(endDate, "yyyy-MM-dd"),
      endTime: format(endDate, "HH:mm"),
      isActive: event.is_active,
      sendNotification: false,
    });
    setEditingEvent(event);
    setIsEditOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.startDate || !formData.startTime || !formData.endDate || !formData.endTime) {
      toast.error("Please fill all required fields");
      return;
    }

    const startsAt = new Date(`${formData.startDate}T${formData.startTime}`).toISOString();
    const endsAt = new Date(`${formData.endDate}T${formData.endTime}`).toISOString();

    try {
      if (editingEvent) {
        // Update existing event
        const { error } = await supabase
          .from("exhibition_events")
          .update({
            name: formData.name,
            description: formData.description || null,
            starts_at: startsAt,
            ends_at: endsAt,
            is_active: formData.isActive,
          })
          .eq("id", editingEvent.id);

        if (error) throw error;
        toast.success("Event updated successfully");
      } else {
        // Create new event
        const { error } = await supabase
          .from("exhibition_events")
          .insert({
            name: formData.name,
            description: formData.description || null,
            starts_at: startsAt,
            ends_at: endsAt,
            is_active: formData.isActive,
            created_by: user?.id,
          });

        if (error) throw error;
        toast.success("Event created successfully");

        // Send notification if enabled
        if (formData.sendNotification && formData.isActive) {
          await sendEventNotification(formData.name, startsAt);
        }
      }

      setIsEditOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("Failed to save event");
    }
  };

  const sendEventNotification = async (eventName: string, startsAt: string) => {
    try {
      // Get all users to notify
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id");

      if (!profiles) return;

      // Create notifications for all users
      const notifications = profiles.map(profile => ({
        user_id: profile.id,
        type: "exhibition_event",
        title: "🌍 Love Exhibition Coming Up!",
        body: `${eventName} starts ${format(new Date(startsAt), "EEEE 'at' h:mm a")}`,
        data: { event_name: eventName, starts_at: startsAt },
      }));

      // Insert in batches
      const batchSize = 100;
      for (let i = 0; i < notifications.length; i += batchSize) {
        const batch = notifications.slice(i, i + batchSize);
        await supabase.from("notifications").insert(batch);
      }

      toast.success("Notifications sent to all users");
    } catch (error) {
      console.error("Error sending notifications:", error);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm("Are you sure you want to delete this event?")) return;

    try {
      const { error } = await supabase
        .from("exhibition_events")
        .delete()
        .eq("id", eventId);

      if (error) throw error;
      toast.success("Event deleted");
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  const handleToggleActive = async (event: ExhibitionEvent) => {
    try {
      const { error } = await supabase
        .from("exhibition_events")
        .update({ is_active: !event.is_active })
        .eq("id", event.id);

      if (error) throw error;
      fetchEvents();
    } catch (error) {
      console.error("Error toggling event:", error);
    }
  };

  const getEventStatus = (event: ExhibitionEvent) => {
    const now = new Date();
    const start = new Date(event.starts_at);
    const end = new Date(event.ends_at);

    if (!event.is_active) return { label: "Draft", color: "bg-gray-500" };
    if (now < start) return { label: "Scheduled", color: "bg-blue-500" };
    if (now >= start && now <= end) return { label: "Live", color: "bg-green-500" };
    return { label: "Ended", color: "bg-gray-500" };
  };

  return (
    <div className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/40 dark:border-gray-700/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-foreground">Event Scheduler</h3>
          </div>
          
          <CreateExhibitionEventModal 
            onEventCreated={fetchEvents}
            trigger={
              <Button size="sm" className="gap-2 rounded-full">
                <Plus className="w-4 h-4" />
                Schedule Event
              </Button>
            }
          />
          
          {/* Edit Event Dialog */}
          <Dialog open={isEditOpen} onOpenChange={(open) => {
            setIsEditOpen(open);
            if (!open) resetForm();
          }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  {editingEvent ? "Edit Event" : "Schedule New Event"}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Event Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Valentine's Love Exhibition"
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Special event for Valentine's Day..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Start Date *</label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Start Time *</label>
                    <Input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">End Date *</label>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">End Time *</label>
                    <Input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">Activate Immediately</span>
                  </div>
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                </div>

                {!editingEvent && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">Send Push Notifications</span>
                    </div>
                    <Switch
                      checked={formData.sendNotification}
                      onCheckedChange={(checked) => setFormData({ ...formData, sendNotification: checked })}
                    />
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setIsEditOpen(false);
                      resetForm();
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleSubmit}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingEvent ? "Update" : "Create"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Events list */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 rounded-full border-2 border-pop border-t-transparent animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8">
            <CalendarDays className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No events scheduled</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => {
              const status = getEventStatus(event);
              return (
                <div
                  key={event.id}
                  className="p-4 rounded-xl bg-muted/30 border border-muted/50"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-foreground">{event.name}</h4>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs text-white font-medium",
                          status.color
                        )}>
                          {status.label}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEditEvent(event)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteEvent(event.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <CalendarDays className="w-4 h-4" />
                      <span>{format(new Date(event.starts_at), "MMM d, yyyy")}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>
                        {format(new Date(event.starts_at), "h:mm a")} - {format(new Date(event.ends_at), "h:mm a")}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-muted/50">
                    <span className="text-xs text-muted-foreground">
                      {event.is_active ? "Active" : "Draft"}
                    </span>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={event.is_active}
                        onCheckedChange={() => handleToggleActive(event)}
                      />
                      {status.label === "Scheduled" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => sendEventNotification(event.name, event.starts_at)}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Notify
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEventScheduler;
